import { useMemo } from 'react';

export interface UseTableSelectionProps {
  enableSelection: boolean;
  selectedRowKeys: string[];
  rowData: any[];
  rowKeyField: string;
  onSelectionChange?: (selectedKeys: string[]) => void;
}

export interface UseTableSelectionReturn {
  isEveryVisibleRowSelected: boolean;
  onBatchSelectButtonPress: () => void;
  onRowSelectionToggle: (rowKey: string) => void;
}

export const useTableSelection = ({
  enableSelection,
  selectedRowKeys,
  rowData,
  rowKeyField,
  onSelectionChange,
}: UseTableSelectionProps): UseTableSelectionReturn => {
  const isEveryVisibleRowSelected = useMemo(() => {
    if (!enableSelection || rowData.length === 0) {
      return false;
    }

    return rowData.every((row) =>
      selectedRowKeys.includes(String(row[rowKeyField])),
    );
  }, [enableSelection, rowData, selectedRowKeys, rowKeyField]);

  const onBatchSelectButtonPress = () => {
    if (!onSelectionChange) {
      return;
    }

    if (isEveryVisibleRowSelected) {
      // Deselect all visible rows
      const visibleKeys = rowData.map((row) => String(row[rowKeyField]));
      const newSelection = selectedRowKeys.filter(
        (key) => !visibleKeys.includes(key),
      );

      onSelectionChange(newSelection);
    } else {
      // Select all visible rows
      const visibleKeys = rowData.map((row) => String(row[rowKeyField]));
      const newSelections = visibleKeys.filter(
        (key) => !selectedRowKeys.includes(key),
      );

      onSelectionChange([...selectedRowKeys, ...newSelections]);
    }
  };

  const onRowSelectionToggle = (rowKey: string) => {
    if (!onSelectionChange) {
      return;
    }

    if (selectedRowKeys.includes(rowKey)) {
      onSelectionChange(selectedRowKeys.filter((key) => key !== rowKey));
    } else {
      onSelectionChange([...selectedRowKeys, rowKey]);
    }
  };

  return {
    isEveryVisibleRowSelected,
    onBatchSelectButtonPress,
    onRowSelectionToggle,
  };
};
